import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Tag } from "lucide-react";
import { api, formatApiError } from "../../../api";
import { DESK } from "../../../constants/testIds";
import { fadeUp } from "../../../lib/motion";
import { Input } from "../../../components/ui/input";
import { StatusPill } from "../../../components/common/StatusPill";
import { toast } from "../../../components/ui/sonner";
import { FormField } from "../../../components/common/FormField";
import { SubmitButton } from "../../../components/common/SubmitButton";
import { useSuccessPulse } from "../../../hooks/useSuccessPulse";

function todayIso() { return new Date().toISOString().slice(0, 10); }

// UX-FD-03 §1 SCR-P02-86 + checklist item 8: the org-level guardrail here is real and already
// enforced server-side — `routers/promotions.py`'s `create_promotion` forcibly pins a
// non-Super-Admin's `branch_scope` to their OWN branch regardless of what's submitted (a
// Manager literally cannot create an org-wide promotion, verified). No branch-scope picker is
// even offered below since it would be silently overridden anyway — honest UI, not a
// non-functional control.
export default function LocalOffers() {
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState("");
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(todayIso());
  const [success, pulse] = useSuccessPulse();

  const listQ = useQuery({ queryKey: ["desk-mgr-offers"], queryFn: async () => (await api.get("/promotions")).data.promotions });

  const submitMutation = useMutation({
    mutationFn: () => api.post("/promotions", {
      code, rules: { discount_percent: Number(discount) },
      valid_window: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` },
    }),
    onSuccess: () => pulse(() => {
      toast.success(`Offer "${code}" created for your branch.`);
      setCode(""); setDiscount("");
      queryClient.invalidateQueries({ queryKey: ["desk-mgr-offers"] });
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={DESK.mgrOffersPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Local offers</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Branch-local promotions — always scoped to your own branch, never org-wide.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <FormField required testId={DESK.mgrOfferCodeInput} placeholder="Offer code (e.g. ANDHERI10) *" value={code} onChange={(v) => setCode(v.toUpperCase())} />
        <FormField required type="number" testId={DESK.mgrOfferDiscountInput} placeholder="Discount % *" value={discount} onChange={setDiscount} />
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-medium" style={{ color: "#68686E" }}>Valid from</label><Input data-testid={DESK.mgrOfferStartInput} type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><label className="text-xs font-medium" style={{ color: "#68686E" }}>Valid to</label><Input data-testid={DESK.mgrOfferEndInput} type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
        </div>
        <SubmitButton
          testId={DESK.mgrOfferSubmitButton}
          disabled={!code.trim() || !discount}
          pending={submitMutation.isPending} success={success}
          onClick={() => submitMutation.mutate()}
          fullWidth label="Create offer" successLabel="Created"
        />
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.08)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="flex items-center gap-1.5 text-sm font-semibold px-4 py-3" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}><Tag size={15} /> Offers</p>
        {listQ.data?.length === 0 && <p className="p-4 text-sm" style={{ color: "#68686E" }}>No promotions yet.</p>}
        {listQ.data?.map((p) => (
          <div key={p.id} data-testid={DESK.mgrOfferRow(p.code)} className="flex items-center justify-between px-4 py-2.5 text-sm" style={{ borderBottom: "1px solid #F4F3EF" }}>
            <div>
              <span className="font-semibold" style={{ color: "#141416" }}>{p.code}</span>
              <span className="ml-2" style={{ color: "#68686E" }}>{p.rules?.discount_percent ?? "—"}% off</span>
            </div>
            <StatusPill status={p.branch_scope ? "active" : "trial"} label={p.branch_scope ? "This branch" : "Org-wide"} />
          </div>
        ))}
      </motion.div>
    </div>
  );
}
